import React from "react";
import { useApp } from "./store.jsx";
import { expansionNameFor } from "./api.jsx";

const { Icon } = window.CardboardmarketDesignSystem_2a3239;

/*
 * PRINTINGS AS PICTURES, for an operator choosing which one a listing is.
 *
 * The owner, 2026-10-20: the "Fix this listing" dialog should show "the images
 * or different versions of a card to be able to check visually if that's the
 * correct one". It was a select of set names and ids, which an operator could
 * only check by opening Cardmarket in another tab.
 *
 * The look is the post form's printing tiles — a 5:7 frame, a caption, a meta
 * line — copied rather than shared because those live inside `PostScreen`.
 * What differs is the job: the post form asks a seller two questions (which
 * set, then which copy inside it); an operator correcting a listing wants every
 * product laid out at once, so a set's copies are tiles of their own under the
 * set's heading.
 *
 * THE PICTURE IS THE LISTING'S, not the chip's: `listingPicture` is what a
 * listing pointing at that product would carry (`listingPicturesFor` on the
 * server), so a blank frame here is a blank frame on the advert.
 */

/**
 * The answer of `/api/catalog/printings` as groups of tiles, one tile per
 * product a listing can point at, in the server's order (by set).
 *
 * `set` is the English name and the group's key; `label` is what is drawn —
 * the Spanish name to a Spanish interface where the catalogue holds one.
 */
export function printingTilesOf(answer, loc) {
  const groups = [];
  const bySet = new Map();
  for (const p of (answer && answer.printings) || []) {
    const set = p.expansion || p.setName || '';
    const label = expansionNameFor(p, loc) || set;
    let group = bySet.get(set);
    if (!group) { group = { key: `set:${set}`, set, label, tiles: [] }; bySet.set(set, group); groups.push(group); }
    const copies = p.versions && p.versions.length > 1 ? p.versions : null;
    if (copies) {
      for (const v of copies) {
        group.tiles.push({
          productId: v.productId, set, label, version: v.label || p.printingName || p.rarity || null,
          picture: v.listingPicture || null,
        });
      }
    } else {
      group.tiles.push({
        productId: p.productId, set, label, version: p.printingName || p.rarity || null,
        picture: p.listingPicture || null,
      });
    }
  }
  return groups;
}

/** A 5:7 frame that holds its shape whether or not a picture ever arrives. */
export function PictureFrame({ picture, sizes = '120px', alt = '' }) {
  return (
    <span className="cbm-ptile__frame">
      {picture && picture.url ? (
        <img src={picture.url} srcSet={picture.srcset || undefined} sizes={sizes} alt={alt}
          loading="lazy" decoding="async" />
      ) : null}
    </span>
  );
}

/**
 * One radio group over every tile of every set. Arrow keys move through the
 * tiles in reading order and choose as they go, as a radio group does; Tab
 * enters the group on the chosen tile and leaves it.
 */
export function PrintingTiles({ groups, value, current, onChange, loading, labelledBy }) {
  const { t } = useApp();
  const refs = React.useRef(new Map());
  const flat = groups.flatMap((g) => g.tiles);
  const tabStop = flat.some((x) => String(x.productId) === value) ? value : (flat[0] && String(flat[0].productId));

  const onKeyDown = (e, index) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    const jump = e.key === 'Home' ? 0 : e.key === 'End' ? flat.length - 1 : null;
    if (step === undefined && jump === null) return;
    e.preventDefault();
    const next = jump ?? (index + step + flat.length) % flat.length;
    const id = String(flat[next].productId);
    onChange(id);
    const el = refs.current.get(id);
    if (el) el.focus();
  };

  if (loading) {
    // The shape of what replaces it: one heading and a row of empty frames.
    return (
      <div role="group" aria-busy="true" aria-labelledby={labelledBy}>
        <div className="cbm-ptile__set">{' '}</div>
        <div className="cbm-ptile__grid">
          {[0, 1, 2].map((i) => (
            <span key={i} className="cbm-ptile" aria-hidden="true">
              <PictureFrame picture={null} />
              <span className="cbm-ptile__caption">{' '}</span>
              <span className="cbm-ptile__meta">{' '}</span>
            </span>
          ))}
        </div>
      </div>
    );
  }

  let index = -1;
  return (
    <div role="radiogroup" aria-labelledby={labelledBy} className="cbm-ptile__groups">
      {groups.map((g) => (
        <div key={g.key} role="group" aria-label={g.label || g.set || t("Set not named")}>
          <div className="cbm-ptile__set">{g.label || g.set || t("Set not named")}</div>
          <div className="cbm-ptile__grid">
            {g.tiles.map((tile) => {
              index += 1;
              const at = index;
              const id = String(tile.productId);
              const chosen = id === value;
              const isCurrent = current != null && Number(current) === Number(tile.productId);
              const caption = [tile.label || tile.set || t("Set not named"), tile.version].filter(Boolean).join(' | ');
              return (
                <button key={id} type="button" role="radio" aria-checked={chosen}
                  ref={(el) => { if (el) refs.current.set(id, el); else refs.current.delete(id); }}
                  tabIndex={id === tabStop ? 0 : -1}
                  className={`cbm-ptile${chosen ? ' cbm-ptile--chosen' : ''}`}
                  onClick={() => onChange(id)}
                  onKeyDown={(e) => onKeyDown(e, at)}>
                  <PictureFrame picture={tile.picture} />
                  {chosen ? (
                    <span className="cbm-ptile__check" aria-hidden="true"><Icon name="check" size={14} /></span>
                  ) : null}
                  {isCurrent ? <span className="cbm-ptile__current">{t("Current")}</span> : null}
                  <span className="cbm-ptile__caption">{caption}</span>
                  <span className="cbm-ptile__meta">#{tile.productId}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
